/**
 * AgentHistoryPage — shows past agent runs (#1 persistent history)
 *
 * Fetches from GET /api/agent/history and displays each run as a card
 * with task, status badge, duration, and token count.
 */
import { useEffect, useState } from 'react'
import { CheckCircle2, XCircle, Clock, Loader2, Bot, RefreshCw } from 'lucide-react'
import { getSupabase } from '@/hooks/useAuth'

import { API_BASE } from '@/lib/config'

interface AgentRun {
  id: string
  task: string
  status: 'running' | 'paused' | 'completed' | 'failed' | 'stopped'
  mode: string
  metrics?: { total_tokens?: number; duration_seconds?: number } | null
  created_at: string
  completed_at?: string | null
}

function StatusBadge({ status }: { status: AgentRun['status'] }) {
  const map = {
    completed: { icon: <CheckCircle2 size={12} />, label: 'Fullført', cls: 'badge-success' },
    failed:    { icon: <XCircle size={12} />,       label: 'Feilet',   cls: 'badge-error' },
    paused:    { icon: <Clock size={12} />,          label: 'Pauset',   cls: 'badge-neutral' },
    stopped:   { icon: <Clock size={12} />,          label: 'Stoppet',  cls: 'badge-neutral' },
    running:   { icon: <Loader2 size={12} className="spin" />, label: 'Kjører', cls: 'badge-running' },
  }
  const { icon, label, cls } = map[status] ?? map.stopped
  return (
    <span className={`agent-history-badge ${cls}`}>
      {icon}
      {label}
    </span>
  )
}

function formatDuration(seconds?: number): string {
  if (!seconds) return ''
  if (seconds < 60) return `${Math.round(seconds)}s`
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('no-NO', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  })
}

export function AgentHistoryPage() {
  const [runs, setRuns] = useState<AgentRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHistory = async () => {
    const sb = getSupabase()
    const { data: { session } } = await sb.auth.getSession()
    if (!session?.access_token) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE}/api/agent/history?limit=30`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setRuns(data.runs ?? [])
    } catch (_e) {
      setError('Kunne ikke laste historikk. Prøv igjen.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchHistory() }, [])

  return (
    <div className="agent-history-page">
      <div className="agent-history-header">
        <div className="agent-history-title">
          <Bot size={18} />
          <span>Agent-historikk</span>
        </div>
        <button className="agent-history-refresh" onClick={fetchHistory} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
        </button>
      </div>

      {loading && (
        <div className="agent-history-loading">
          <Loader2 size={20} className="spin" />
          <span>Laster historikk…</span>
        </div>
      )}

      {error && (
        <div className="agent-history-error">{error}</div>
      )}

      {!loading && !error && runs.length === 0 && (
        <div className="agent-history-empty">
          <Bot size={32} style={{ opacity: 0.3 }} />
          <p>Ingen agent-kjøringer ennå.</p>
          <p style={{ fontSize: 12, opacity: 0.5 }}>Start en agent-oppgave for å se historikk her.</p>
        </div>
      )}

      {!loading && runs.length > 0 && (
        <div className="agent-history-list">
          {runs.map(run => (
            <div key={run.id} className="agent-history-card">
              <div className="agent-history-card-top">
                <StatusBadge status={run.status} />
                <span className="agent-history-card-date">{formatDate(run.created_at)}</span>
              </div>
              <p className="agent-history-card-task">{run.task}</p>
              <div className="agent-history-card-meta">
                {run.metrics?.total_tokens !== undefined && (
                  <span>{run.metrics.total_tokens.toLocaleString('no')} tokens</span>
                )}
                {run.metrics?.duration_seconds !== undefined && (
                  <span>{formatDuration(run.metrics.duration_seconds)}</span>
                )}
                <span className={`agent-history-mode agent-history-mode--${run.mode}`}>
                  {run.mode === 'power' ? '⚡ Power' : '🛡 Safe'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
